/**
 * LEGACY (save v2) item economy, kept so old screens and tools still compile. Since save v3 the
 * Altar Forge (forge.ts: temper, masterwork rolls, salvage) replaces these: Shards are always 0,
 * so the Shard-priced operations below can no longer be afforded. Reforge is still live
 * (profile.reforgeItem now charges Fangsteel + gold).
 *
 * Upgrade (Shards + gold → ilvl), reforge (gold → swap a secondary), masterwork (Shards + gold →
 * +1 effective ilvl at the cap), scrap (item → Shards). PLAN §11. Pure.
 *
 * Gold sinks, sized against rewards.ts income (~420 gold per +2 run, ~1300 at +16):
 *   - upgrades cost UPGRADE_GOLD_PER_SHARD gold per Shard (the main sink while climbing);
 *   - reforging costs REFORGE_GOLD (+ REFORGE_COST Shards);
 *   - fusing gems costs fuseGoldCost(rank) (gems.ts callers, profile.fuseGems);
 *   - masterwork soaks up the Shard + gold surplus once gear sits at the tier cap.
 */
import { BASE_ILVL, MASTERWORK_MAX, MAX_ILVL, rescaleSecondaries } from './items';
import type { Gem, Item, SecondaryStat } from './types';

/** Item levels per upgrade step. */
export const UPGRADE_STEP = 5;

/** Shard cost of one step starting at `fromIlvl` (rises 5 per 10 ilvl above 200). */
export function upgradeStepCost(fromIlvl: number): number {
  return 10 + Math.max(0, Math.floor((fromIlvl - BASE_ILVL) / 10)) * 5;
}

/** Item level after one step from `ilvl`, never past `cap`. */
function stepFrom(ilvl: number, cap: number): number {
  return Math.min(cap, ilvl + UPGRADE_STEP);
}

/** Shards to go from the item's ilvl to `toIlvl` (steps of 5; a partial last step costs a full step). */
export function upgradeCost(item: Item, toIlvl: number): number {
  let cost = 0;
  for (let l = item.ilvl; l < toIlvl; l = stepFrom(l, toIlvl)) cost += upgradeStepCost(l);
  return cost;
}

/** Effective upgrade cap: the tier's max item level, never above MAX_ILVL. */
export function upgradeCap(tierCap: number): number {
  return Math.min(tierCap, MAX_ILVL);
}

/** Gold per Shard of upgrade cost (a 10-Shard step costs 60 gold, a 40-Shard step 240). */
export const UPGRADE_GOLD_PER_SHARD = 6;

/** Gold cost of one upgrade step starting at `fromIlvl`. */
export function upgradeStepGold(fromIlvl: number): number {
  return upgradeStepCost(fromIlvl) * UPGRADE_GOLD_PER_SHARD;
}

/** Gold to go from the item's ilvl to `toIlvl`. */
export function upgradeGoldCost(item: Item, toIlvl: number): number {
  return upgradeCost(item, toIlvl) * UPGRADE_GOLD_PER_SHARD;
}

export type UpgradeResult =
  | { ok: true; item: Item; cost: number; gold: number }
  | { ok: false; reason: 'capped' | 'notEnoughShards' | 'notEnoughGold' | 'badTarget' };

/**
 * Raise `item` to `toIlvl`, capped by the tier's max ilvl. The target must be a whole number of
 * 5-ilvl steps above the item, or exactly the cap. Costs Shards (`cost`) and gold (`gold`).
 * Secondaries are rescaled; gems and masterwork are kept.
 */
export function upgrade(item: Item, toIlvl: number, shards: number, tierCap: number, gold = Infinity): UpgradeResult {
  const cap = upgradeCap(tierCap);
  if (!Number.isInteger(toIlvl) || toIlvl <= item.ilvl) return { ok: false, reason: 'badTarget' };
  if (toIlvl > cap) return { ok: false, reason: 'capped' };
  if ((toIlvl - item.ilvl) % UPGRADE_STEP !== 0 && toIlvl !== cap) return { ok: false, reason: 'badTarget' };
  const cost = upgradeCost(item, toIlvl);
  if (cost > shards) return { ok: false, reason: 'notEnoughShards' };
  const goldCost = cost * UPGRADE_GOLD_PER_SHARD;
  if (goldCost > gold) return { ok: false, reason: 'notEnoughGold' };
  return { ok: true, item: { ...item, ilvl: toIlvl, secondaries: rescaleSecondaries(item, toIlvl) }, cost, gold: goldCost };
}

/** Every reachable target with its cumulative Shard and gold cost (UI upgrade ladder). */
export function upgradeLadder(item: Item, tierCap: number): { ilvl: number; cost: number; gold: number }[] {
  const cap = upgradeCap(tierCap);
  const out: { ilvl: number; cost: number; gold: number }[] = [];
  let cost = 0;
  for (let l = item.ilvl; l < cap; ) {
    cost += upgradeStepCost(l);
    l = stepFrom(l, cap);
    out.push({ ilvl: l, cost, gold: cost * UPGRADE_GOLD_PER_SHARD });
  }
  return out;
}

/** Highest item level affordable with `shards` (and `gold`; the item's own ilvl if none). */
export function maxAffordableIlvl(item: Item, shards: number, tierCap: number, gold = Infinity): number {
  let best = item.ilvl;
  for (const s of upgradeLadder(item, tierCap)) if (s.cost <= shards && s.gold <= gold) best = s.ilvl;
  return best;
}

// ============================================================================
// Masterwork: the cap-surplus sink
// ============================================================================

/** Shards + gold for masterwork step `from → from+1` (from = current masterwork, 0-based). */
export const MASTERWORK_COSTS: readonly { shards: number; gold: number }[] = [
  { shards: 40, gold: 400 },
  { shards: 60, gold: 600 },
  { shards: 80, gold: 800 },
];

/** Cost of the item's next masterwork step (null when maxed). */
export function masterworkStepCost(item: Pick<Item, 'masterwork'>): { shards: number; gold: number } | null {
  const mw = item.masterwork ?? 0;
  return mw >= MASTERWORK_MAX ? null : (MASTERWORK_COSTS[mw] ?? null);
}

export type MasterworkResult =
  | { ok: true; item: Item; shards: number; gold: number }
  | { ok: false; reason: 'notAtCap' | 'maxMasterwork' | 'notEnoughShards' | 'notEnoughGold' };

/**
 * One masterwork step: +1 effective item level (power and HP, not secondaries), up to
 * MASTERWORK_MAX. Only for an item already at the tier's upgrade cap, so it never competes with
 * upgrades; it is kept when a later tier lets the item upgrade further.
 */
export function masterwork(item: Item, shards: number, gold: number, tierCap: number): MasterworkResult {
  if (item.ilvl < upgradeCap(tierCap)) return { ok: false, reason: 'notAtCap' };
  const c = masterworkStepCost(item);
  if (!c) return { ok: false, reason: 'maxMasterwork' };
  if (c.shards > shards) return { ok: false, reason: 'notEnoughShards' };
  if (c.gold > gold) return { ok: false, reason: 'notEnoughGold' };
  return { ok: true, item: { ...item, masterwork: (item.masterwork ?? 0) + 1 }, shards: c.shards, gold: c.gold };
}

export type ReforgeResult = { ok: true; item: Item } | { ok: false; reason: 'alreadyReforged' | 'duplicateStat' | 'badIndex' };

/** Swap secondary `index` to `to`. One reforge per item; can't duplicate the other stat. */
export function reforge(item: Item, index: number, to: SecondaryStat): ReforgeResult {
  const cur = item.secondaries[index];
  if (!cur) return { ok: false, reason: 'badIndex' };
  if (item.reforged) return { ok: false, reason: 'alreadyReforged' };
  if (item.secondaries.some((s, i) => i !== index && s.stat === to) || cur.stat === to) return { ok: false, reason: 'duplicateStat' };
  const secondaries = item.secondaries.map((s, i) => (i === index ? { stat: to, rating: s.rating } : s));
  return { ok: true, item: { ...item, secondaries, reforged: { index, from: cur.stat } } };
}

/** Shard cost of a reforge (flat, cheap: it is a tuning knob, not a sink). */
export const REFORGE_COST = 5;
/** Gold cost of a reforge. */
export const REFORGE_GOLD = 100;

/** Undo a reforge (free; lets the player re-reforge to a different stat). */
export function revertReforge(item: Item): Item {
  if (!item.reforged) return item;
  const { index, from } = item.reforged;
  const secondaries = item.secondaries.map((s, i) => (i === index ? { stat: from, rating: s.rating } : s));
  const out: Item = { ...item, secondaries };
  delete out.reforged;
  return out;
}

const RARITY_SCRAP: Readonly<Record<Item['rarity'], number>> = { common: 0, uncommon: 2, rare: 4, epic: 10, legendary: 40 };

/** Shards from scrapping an item: base 5, +1 per 5 ilvl above 200, + rarity. */
export function scrap(item: Item): number {
  return 5 + Math.floor(Math.max(0, item.ilvl - BASE_ILVL) / 5) + RARITY_SCRAP[item.rarity];
}

/** Scrap returning Shards and the socketed gems (which go back to the stash). */
export function scrapWithGems(item: Item): { shards: number; gems: Gem[] } {
  return { shards: scrap(item), gems: item.gems.filter((g): g is Gem => g !== null).map((g) => ({ ...g })) };
}
